
import React from 'react';
import { User } from '../types';
import { Sparkles, BookOpen, GraduationCap, CheckCircle2, AlertTriangle, ShieldCheck, Zap, CreditCard, MessageCircle } from 'lucide-react';

interface Props {
  onEnter: () => void;
  user: User | null;
}

const LandingPage: React.FC<Props> = ({ onEnter, user }) => {
  /**
   * EXPLICAÇÃO PARA O MESTRE:
   * Esta função pega o seu link do Stripe e adiciona o ID do aluno logado.
   * Quando o Stripe processar o pagamento, ele vai nos devolver esse ID.
   */
  const getStripeUrl = (baseUrl: string) => {
    if (!user) return baseUrl;
    try {
      const url = new URL(baseUrl);
      // 'client_reference_id' é o campo que o Stripe usa para identificar o cliente no nosso banco
      url.searchParams.append('client_reference_id', user.id);
      url.searchParams.append('prefilled_email', user.email);
      return url.toString();
    } catch (e) {
      return baseUrl;
    }
  };

  // LINKS OFICIAIS ATUALIZADOS PELO MESTRE
  const STRIPE_LINKS = {
    BRONZE: getStripeUrl("https://buy.stripe.com/9B68wO1O47UsdGR1CBbjW03"), 
    PRATA: getStripeUrl("https://buy.stripe.com/bJe6oGdwMb6E46h1CBbjW04"),
    OURO: getStripeUrl("https://buy.stripe.com/dRmcN4csI8Yw6ep4ONbjW05")
  };

  const PlanButton = ({ href, label, primary = false }: { href: string, label: string, primary?: boolean }) => (
    <a 
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`w-full py-4 px-6 rounded-2xl font-black text-center transition-all transform hover:scale-[1.02] flex items-center justify-center gap-3 text-sm tracking-wide ${
        primary 
          ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white shadow-xl shadow-orange-900/30' 
          : 'bg-purple-100 dark:bg-white/5 text-purple-700 dark:text-white border border-purple-200 dark:border-white/10 hover:bg-purple-200 dark:hover:bg-white/10'
      }`}
    >
      <CreditCard size={20} />
      {label}
    </a>
  );

  return (
    <div className="flex flex-col items-center justify-center space-y-20 py-12 animate-fade-in">
      
      {!user && (
        <div className="text-center space-y-8 max-w-4xl mx-auto px-4">
          <div className="inline-flex items-center justify-center p-4 rounded-full bg-purple-100 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-500/30 mb-6 backdrop-blur-sm">
            <Sparkles className="text-chroma-accent w-6 h-6 mr-3" />
            <span className="text-purple-700 dark:text-purple-200 text-sm font-black tracking-[0.2em] uppercase">Nova Versão 2.0</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-chroma-text leading-[1.1] tracking-tight">
            Cromoterapia <br className="hidden md:block"/><span className="text-transparent bg-clip-text bg-gradient-to-r from-chroma-accent to-purple-500">Aplicada</span>
            <span className="text-2xl md:text-3xl text-chroma-muted align-top ml-2 font-black opacity-50">V2.0</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-chroma-muted leading-relaxed max-w-3xl mx-auto opacity-90 font-medium">
            Potencialize seus atendimentos terapêuticos com a evolução do sistema oficial de João Caputo. 
            IA avançada e organização metodológica superior.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 pt-6">
            <button 
              onClick={onEnter}
              className="w-full sm:w-auto px-12 py-5 bg-gradient-to-r from-chroma-accent to-purple-600 hover:from-fuchsia-400 hover:to-purple-500 text-white font-black rounded-2xl transition-all shadow-2xl shadow-purple-900/40 flex items-center justify-center gap-3 transform hover:scale-105 text-lg"
            >
              <Zap size={24} className="fill-white" />
              ENTRAR NO PORTAL V2.0
            </button>
          </div>
        </div>
      )}

      <div id="planos" className="w-full max-w-7xl mx-auto px-6 scroll-mt-24">
        <h2 className="text-3xl md:text-4xl font-bold text-chroma-text text-center mb-16">
          {user ? 'Deseja fazer um Upgrade no seu plano?' : 'Escolha seu nível de atuação'}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Free */}
          <div className="bg-chroma-card border border-chroma-border rounded-3xl p-8 flex flex-col hover:border-chroma-accent/50 transition-all shadow-lg opacity-60">
            <h3 className="text-chroma-muted font-black text-xl mb-3 tracking-wide">Iniciante (Free)</h3>
            <div className="text-4xl font-black text-chroma-text mb-6">Gratuito</div>
            <p className="text-base text-chroma-muted mb-8 opacity-80 font-medium">Período de teste.</p>
            <ul className="space-y-4 mb-10 flex-1">
              <li className="flex items-center gap-3 text-base text-chroma-text font-bold"><CheckCircle2 size={20} className="text-purple-500"/> 2 Protocolos / dia</li>
              <li className="flex items-center gap-3 text-base text-chroma-text font-bold"><CheckCircle2 size={20} className="text-purple-500"/> Válido por 30 dias</li>
            </ul>
            <button disabled className="w-full py-4 bg-gray-200 dark:bg-white/5 text-gray-400 rounded-2xl font-black text-sm uppercase tracking-widest">
              {user?.profile === 'Free' ? 'Seu Plano Atual' : 'Plano Inicial'}
            </button>
          </div>

          {/* Bronze */}
          <div className="bg-chroma-card border border-orange-200 dark:border-orange-900/50 rounded-3xl p-8 flex flex-col hover:border-orange-500/50 transition-all shadow-lg">
            <h3 className="text-orange-600 dark:text-orange-400 font-black text-xl mb-3 tracking-wide">Bronze</h3>
            <div className="text-4xl font-black text-chroma-text mb-2">R$ 29,90<span className="text-sm font-bold text-chroma-muted ml-1">/mês</span></div>
            <p className="text-base text-chroma-muted mb-8 opacity-80 font-medium">Terapeutas iniciantes.</p>
            <ul className="space-y-4 mb-10 flex-1">
              <li className="flex items-center gap-3 text-base text-chroma-text font-bold"><CheckCircle2 size={20} className="text-orange-500"/> 5 Protocolos / dia</li>
              <li className="flex items-center gap-3 text-base text-chroma-text font-bold"><CheckCircle2 size={20} className="text-orange-500"/> Histórico Básico</li>
            </ul>
            <PlanButton href={STRIPE_LINKS.BRONZE} label="Assinar Bronze" />
          </div>

          {/* Prata */}
          <div className="bg-chroma-card border border-slate-200 dark:border-slate-600/50 rounded-3xl p-8 flex flex-col hover:border-slate-400/50 transition-all shadow-lg">
            <h3 className="text-slate-600 dark:text-slate-300 font-black text-xl mb-3 tracking-wide">Prata</h3>
            <div className="text-4xl font-black text-chroma-text mb-2">R$ 59,90<span className="text-sm font-bold text-chroma-muted ml-1">/mês</span></div>
            <p className="text-base text-chroma-muted mb-8 opacity-80 font-medium">Profissionais ativos.</p>
            <ul className="space-y-4 mb-10 flex-1">
              <li className="flex items-center gap-3 text-base text-chroma-text font-bold"><CheckCircle2 size={20} className="text-slate-400"/> 15 Protocolos / dia</li>
              <li className="flex items-center gap-3 text-base text-chroma-text font-bold"><CheckCircle2 size={20} className="text-slate-400"/> Gestão de Pacientes</li>
            </ul>
            <PlanButton href={STRIPE_LINKS.PRATA} label="Assinar Prata" />
          </div>

          {/* Ouro */}
          <div className="bg-gradient-to-b from-yellow-50 to-white dark:from-[#2e1c0d] dark:to-[#1a082e] border-4 border-yellow-400 rounded-3xl p-8 flex flex-col hover:shadow-yellow-500/20 transition-all transform hover:-translate-y-2 relative shadow-2xl scale-105 z-10">
            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-white text-xs font-black px-6 py-2 rounded-full uppercase tracking-[0.2em] shadow-xl">
              OPÇÃO MAIS VANTAJOSA
            </div>
            <h3 className="text-yellow-600 dark:text-yellow-400 font-black text-xl mb-3 flex items-center gap-3 tracking-wide"><ShieldCheck size={24}/> Ouro Vitalício</h3>
            <div className="text-4xl font-black text-chroma-text mb-2">R$ 497,00</div>
            <p className="text-base text-yellow-800/80 dark:text-yellow-200/80 mb-8 font-bold">Acesso total e eterno.</p>
            <ul className="space-y-4 mb-10 flex-1">
              <li className="flex items-center gap-3 text-base text-chroma-text font-black"><CheckCircle2 size={20} className="text-yellow-500"/> Protocolos Ilimitados</li>
              <li className="flex items-center gap-3 text-base text-chroma-text font-bold"><CheckCircle2 size={20} className="text-yellow-500"/> Vitalício (Paga 1x)</li>
              <li className="flex items-center gap-3 text-base text-chroma-text font-bold"><CheckCircle2 size={20} className="text-yellow-500"/> Suporte VIP</li>
            </ul>
            <PlanButton href={STRIPE_LINKS.OURO} label="Garantir Vitalício" primary />
          </div>

        </div>
      </div>

      <div className="w-full max-w-5xl px-6">
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-500/30 rounded-3xl p-10 flex flex-col lg:flex-row items-center lg:items-start gap-8 shadow-xl">
          <div className="p-5 bg-red-100 dark:bg-red-900/40 rounded-full shrink-0 animate-pulse">
             <AlertTriangle className="text-red-600 dark:text-red-400 w-10 h-10" />
          </div>
          <div>
            <h3 className="text-red-700 dark:text-red-400 font-black uppercase tracking-[0.2em] mb-4 text-center lg:text-left text-base">Aviso Legal</h3>
            <p className="text-red-800/90 dark:text-red-200/90 text-base md:text-lg leading-relaxed text-justify font-medium">
              Cromoterapia é terapia integrativa. Nunca abandone o tratamento médico convencional. O sistema é uma ferramenta de apoio à decisão do terapeuta.
            </p>
          </div>
        </div>
      </div>

      <footer className="text-center text-sm font-bold text-chroma-muted opacity-60 pb-12 uppercase tracking-[0.3em]">
        © {new Date().getFullYear()} João Caputo V2.0 • Sistema de Gestão Terapêutica
      </footer>

    </div>
  );
};

export default LandingPage;
